import Guid from "_shared/guids/guid";
import UserCoworker from "users/information/coworker-information/user-coworker";
import ProposalReviewerCandidateSchema, {
  ProposalReviewerCandidateJSON,
  ProposalReviewerCandidateSource,
} from "work/proposals/reviewers/_schemas/proposal-reviewer-candidate-schema";

/**
 * Somebody a team leader could add as a reviewer (issue #5404): a coworker, or somebody they have
 * worked with on a proposal or a work agreement.
 *
 * The person is carried as a {@link UserCoworker} because the fields are the same ones, and the
 * reviewer popover already draws, names and adds a coworker; being one is what {@link source} says.
 */
export default class ProposalReviewerCandidate {
  public static fromJSON(json: ProposalReviewerCandidateJSON): ProposalReviewerCandidate {
    const validated = ProposalReviewerCandidateSchema.validateJSON(json);
    const { source, isCounterparty, ...person } = validated;
    return new ProposalReviewerCandidate(UserCoworker.fromJSON(person), source, isCounterparty);
  }

  public static fromJSONArray(jsonArray: ProposalReviewerCandidateJSON[]): ProposalReviewerCandidate[] {
    return jsonArray.map((json) => ProposalReviewerCandidate.fromJSON(json));
  }

  public readonly person: UserCoworker;
  public readonly source: ProposalReviewerCandidateSource;
  /**
   * On the other side of this proposal. Listed so the leader can see why they cannot pick them; the
   * server refuses anyone who represents both parties, so adding them would lock them out of their
   * own side.
   */
  public readonly isCounterparty: boolean;

  public get id(): Guid {
    return this.person.userId;
  }

  public get userId(): Guid {
    return this.person.userId;
  }

  public get isCoworker(): boolean {
    return this.source === "coworker";
  }

  public constructor(person: UserCoworker, source: ProposalReviewerCandidateSource, isCounterparty: boolean) {
    this.person = person;
    this.source = source;
    this.isCounterparty = isCounterparty;
  }

  public isEqualTo(other?: ProposalReviewerCandidate): boolean {
    return (
      other !== undefined &&
      this.userId.isEqualTo(other.userId) &&
      this.source === other.source &&
      this.isCounterparty === other.isCounterparty
    );
  }

  public toJSON(): ProposalReviewerCandidateJSON {
    return { ...this.person.toJSON(), source: this.source, isCounterparty: this.isCounterparty };
  }
}
